export const UI_ANCHOR_IDS = {
  shellFrame: 'shell-frame',
  gameBoard: 'game-board',
  localAvatar: 'local-avatar',
  opponentAvatar: 'opponent-avatar',
  deckZone: 'deck-zone',
  prepZone: 'prep-zone',
  handRail: 'hand-rail',
} as const;

export function getPreparedCardAnchorId(cardId: string) {
  return `prepared-card:${cardId}`;
}

export interface UiAnchorMetadata {
  kind?: string;
  ownerId?: string | number;
}

export interface UiAnchorSnapshot {
  id: string;
  rect: DOMRect;
  center: { x: number; y: number };
  updatedAt: number;
  meta?: UiAnchorMetadata;
}

type UiAnchorListener = () => void;

class UiAnchorRegistry {
  private anchors = new Map<string, UiAnchorSnapshot>();
  private listeners = new Set<UiAnchorListener>();

  update(id: string, rect: DOMRect | DOMRectReadOnly, meta?: UiAnchorMetadata) {
    const nextRect = new DOMRect(rect.x, rect.y, rect.width, rect.height);
    this.anchors.set(id, {
      id,
      rect: nextRect,
      center: {
        x: nextRect.left + nextRect.width / 2,
        y: nextRect.top + nextRect.height / 2,
      },
      updatedAt: Date.now(),
      meta,
    });
    this.emit();
  }

  updateFromElement(id: string, element: Element | null, meta?: UiAnchorMetadata) {
    if (!element) {
      return;
    }
    this.update(id, element.getBoundingClientRect(), meta);
  }

  unregister(id: string) {
    if (this.anchors.delete(id)) {
      this.emit();
    }
  }

  clearByPrefix(prefix: string) {
    let changed = false;
    for (const key of Array.from(this.anchors.keys())) {
      if (key.startsWith(prefix)) {
        this.anchors.delete(key);
        changed = true;
      }
    }
    if (changed) {
      this.emit();
    }
  }

  get(id: string) {
    return this.anchors.get(id);
  }

  getRect(id: string) {
    return this.anchors.get(id)?.rect;
  }

  getAll() {
    return Array.from(this.anchors.values());
  }

  subscribe(listener: UiAnchorListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit() {
    this.listeners.forEach((listener) => listener());
  }
}

export const uiAnchorRegistry = new UiAnchorRegistry();
